import React from 'react';
import { Settings, TrendingUp, Activity, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useDashboard } from '../../DashboardContext';
import { S, SCENARIO_LABELS } from '../../lib/constants';
import Input from '../shared/Input';
import InfoTooltip from '../shared/InfoTooltip';

function LiveDataBadge({ meta, onRefresh }) {
  if (!meta) return null;

  const age = meta.fetchedAt ? Date.now() - meta.fetchedAt : null;
  const ageMinutes = age ? Math.floor(age / 60000) : null;
  const ageHours = age ? Math.floor(age / 3600000) : null;
  const ageStr = ageMinutes === null ? '' :
    ageHours >= 24 ? `${Math.floor(ageHours / 24)}d ago` :
    ageHours >= 1 ? `${ageHours}h ago` :
    ageMinutes >= 1 ? `${ageMinutes}m ago` : 'just now';

  const isLive = meta.source && !meta.stale;
  const StatusIcon = isLive ? Wifi : WifiOff;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium"
        style={{
          background: meta.loading ? '#FEF3C7' : isLive ? '#ECFDF5' : '#FFF7ED',
          color: meta.loading ? '#333333' : isLive ? '#16A34A' : '#92700C',
          border: `1px solid ${meta.loading ? '#FDE68A' : isLive ? '#A7F3D0' : '#FED7AA'}`,
        }}>
        <StatusIcon className="w-3 h-3" />
        {meta.loading ? 'Fetching live data...' : (
          <>
            {meta.source || 'offline'}{ageStr ? ` · ${ageStr}` : ''}
            {meta.stale && ' (stale)'}
          </>
        )}
      </div>
      <button
        onClick={onRefresh}
        disabled={meta.loading}
        className="p-1 rounded-full transition-colors hover:opacity-80 disabled:opacity-40"
        title="Refresh market data"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${meta.loading ? 'animate-spin' : ''}`} style={{ color: '#525252' }} />
      </button>
    </div>
  );
}

export default function MarketSettingsSection() {
  const {
    timeHorizon, setTimeHorizon,
    btcPrice, setBtcPrice,
    priceScenario, setPriceScenario,
    electricityCost, setElectricityCost,
    hostingFixed, setHostingFixed,
    poolFee, setPoolFee,
    blockReward, setBlockReward,
    difficulty, setDifficulty,
    difficultyGrowth, setDifficultyGrowth,
    uptimeFactor, setUptimeFactor,
    startingTreasury, setStartingTreasury,
    startingCash, setStartingCash,
    marketDataMeta,
    refreshMarketData,
  } = useDashboard();

  return (
    <div className="space-y-6">
      <div className="overflow-hidden" style={{ ...S.card, borderRadius: '8px' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: '#525252', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'IBM Plex Mono, monospace' }}>
              <Settings className="w-4 h-4" style={{ color: '#92700C' }} /> Market Assumptions
            </h3>
            <LiveDataBadge meta={marketDataMeta} onRefresh={() => refreshMarketData(true)} />
          </div>

          {/* Row 1: Price & Time */}
          <div className="mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2.5 flex items-center gap-1.5" style={{ color: '#92700C', fontFamily: 'IBM Plex Mono, monospace' }}>
              <TrendingUp className="w-3 h-3" /> Price & Time Horizon
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input label="Years" value={timeHorizon} onChange={setTimeHorizon} min={1} max={21} />
              <div>
                <label className="block mb-1.5 font-medium" style={{ color: '#525252', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '10px', fontFamily: 'IBM Plex Mono, monospace' }}>Price Scenario</label>
                <select value={priceScenario} onChange={(e) => setPriceScenario(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm focus:outline-none transition-colors"
                  style={{ background: '#F5F5F5', border: '1px solid #E5E5E5', color: '#1A1A1A', borderRadius: '4px' }}>
                  {Object.entries(SCENARIO_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </div>
              <Input label="BTC Price ($)" value={btcPrice} onChange={setBtcPrice} min={10000} max={1000000} step={1000} />
              <Input label="Pool Fee %" value={poolFee * 100} onChange={(v) => setPoolFee(v / 100)} min={0} max={5} step={0.1} />
            </div>
          </div>

          {/* Row 2: Mining Difficulty */}
          <div className="mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2.5 flex items-center gap-1.5" style={{ color: '#92700C', fontFamily: 'IBM Plex Mono, monospace' }}>
              <Activity className="w-3 h-3" /> Network Difficulty
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <InfoTooltip text="Current Bitcoin network mining difficulty in trillions (T). Find this on any block explorer. Higher difficulty = fewer BTC mined per unit of hashrate.">
                <Input label="Difficulty (T)" value={difficulty} onChange={setDifficulty} min={1} max={1000} step={0.01} />
              </InfoTooltip>
              <InfoTooltip text="A stated planning assumption of 10%/yr, not a forecast and not derived from trailing data. Deriving it failed badly: a 3-year fit returned 37%/yr in 2026 while difficulty was actually falling, and compounding that over 15 years implied a 114x rise that no energy budget supports. The observed trailing rates are shown beneath so you can override deliberately.">
                <div>
                  <Input label="Difficulty Growth %/yr" value={difficultyGrowth * 100} onChange={(v) => setDifficultyGrowth(v / 100)} min={-20} max={100} step={1} />
                  {(marketDataMeta?.observedGrowth3y != null || marketDataMeta?.observedGrowth1y != null) && (
                    <div className="text-[10px] mt-1" style={{ color: '#737373', fontFamily: 'IBM Plex Mono, monospace' }}>
                      observed:{' '}
                      {marketDataMeta.observedGrowth1y != null && (
                        <>1yr <strong style={{ color: marketDataMeta.observedGrowth1y < 0 ? '#15803D' : '#1A1A1A' }}>
                          {(marketDataMeta.observedGrowth1y * 100).toFixed(1)}%
                        </strong></>
                      )}
                      {marketDataMeta.observedGrowth3y != null && (
                        <> · 3yr <strong style={{ color: '#1A1A1A' }}>{(marketDataMeta.observedGrowth3y * 100).toFixed(1)}%</strong></>
                      )}
                    </div>
                  )}
                </div>
              </InfoTooltip>
              <Input label="$/kWh" value={electricityCost} onChange={setElectricityCost} min={0.01} max={0.30} step={0.005} />
              <Input label="Hosting $/miner/mo" value={hostingFixed} onChange={setHostingFixed} min={0} max={500} />
              <InfoTooltip text="Fleet uptime accounts for reboots, maintenance windows, and outages. Well-run hosted fleets average 95-98%.">
                <Input label="Uptime %" value={Math.round(uptimeFactor * 100)} onChange={(v) => setUptimeFactor(v / 100)} min={50} max={100} step={1} />
              </InfoTooltip>
            </div>
          </div>

          {/* Row 3: Block Reward & Starting Balances */}
          <div className="mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2.5 flex items-center gap-1.5" style={{ color: '#16A34A', fontFamily: 'IBM Plex Mono, monospace' }}>
              Starting Balances & Block Reward
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Input label="Block Reward" value={blockReward} onChange={setBlockReward} min={0.1} max={50} step={0.001} />
              <Input label="Starting Treasury BTC" value={startingTreasury} onChange={setStartingTreasury} min={0} max={1000} step={0.1} />
              <Input label="Starting Cash $" value={startingCash} onChange={setStartingCash} min={0} max={25000000} step={1000} />
            </div>
          </div>

          {/* Derived info bar */}
          <div className="px-4 py-2.5 flex items-center gap-6 flex-wrap" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '8px' }}>
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#92700C', fontFamily: 'IBM Plex Mono, monospace' }}>Derived</span>
            <span className="text-xs" style={{ color: '#525252', fontFamily: 'IBM Plex Mono, monospace' }}>
              Network Hashrate: <strong style={{ color: '#1A1A1A' }}>{marketDataMeta?.networkHashrateEH || ((difficulty * 1e12 * Math.pow(2, 32)) / (600 * 1e18)).toFixed(0)} EH/s</strong>
            </span>
            <span className="text-xs" style={{ color: '#525252', fontFamily: 'IBM Plex Mono, monospace' }}>
              Monthly Difficulty Growth: <strong style={{ color: '#1A1A1A' }}>{((Math.pow(1 + difficultyGrowth, 1/12) - 1) * 100).toFixed(2)}%</strong>
            </span>
            {marketDataMeta?.difficultyAdjustment && (
              <>
                <span className="text-xs" style={{ color: '#525252', fontFamily: 'IBM Plex Mono, monospace' }}>
                  Next Adjustment: <strong style={{ color: marketDataMeta.difficultyAdjustment.changePct >= 0 ? '#DC2626' : '#16A34A' }}>
                    {marketDataMeta.difficultyAdjustment.changePct >= 0 ? '+' : ''}{marketDataMeta.difficultyAdjustment.changePct}%
                  </strong>
                </span>
                <span className="text-xs" style={{ color: '#525252', fontFamily: 'IBM Plex Mono, monospace' }}>
                  Epoch: <strong style={{ color: '#1A1A1A' }}>{marketDataMeta.difficultyAdjustment.progressPct}%</strong> ({marketDataMeta.difficultyAdjustment.remainingBlocks} blocks left)
                </span>
              </>
            )}
            {marketDataMeta?.blockHeight && (
              <span className="text-xs" style={{ color: '#525252', fontFamily: 'IBM Plex Mono, monospace' }}>
                Block: <strong style={{ color: '#1A1A1A' }}>#{marketDataMeta.blockHeight.toLocaleString()}</strong>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
